import {useEffect, useState} from 'react'
import {PencilIcon} from '@heroicons/react/24/outline'
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from '@headlessui/react'
import {AddressFields, type AddressFieldValue} from './AddressFields.tsx'

export type {AddressFieldValue} from './AddressFields.tsx'

type AddressFieldProps = {
    label?: string
    value: AddressFieldValue
    onChange: (value: AddressFieldValue) => void
    id?: string
    name?: string
    placeholder?: string
    disabled?: boolean
    required?: boolean
    className?: string
    asText?: boolean
}

function formatAddress(fields: AddressFieldValue) {
    const p1 = [fields.street_name, fields.house_number].filter(Boolean).join(' ')
    const p2 = [fields.postal_code, fields.city_name].filter(Boolean).join(' ')
    const p3 = fields.country?.trim() || ''
    return [p1, p2, p3].filter(Boolean).join(', ')
}

function withDefaults(value: AddressFieldValue): AddressFieldValue {
    return {
        street_name: value.street_name || '',
        house_number: value.house_number || '',
        city_name: value.city_name || '',
        postal_code: value.postal_code || '',
        country: value.country || 'NL',
    }
}

export default function AddressField({
                                         label = 'Adres',
                                         value,
                                         onChange,
                                         id,
                                         name,
                                         placeholder = 'Bijv. Damrak 1, 1012LG Amsterdam, NL',
                                         disabled = true,
                                         required,
                                         className = '',
                                         asText = false,
                                     }: AddressFieldProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [draft, setDraft] = useState<AddressFieldValue>(withDefaults(value))

    useEffect(() => {
        setDraft(withDefaults(value))
    }, [value])

    function openModal() {
        setDraft(withDefaults(value))
        setIsOpen(true)
    }

    function handleSave() {
        onChange(draft)
        setIsOpen(false)
    }

    const displayValue = formatAddress(value)
    const isCountryEmpty = !draft.country.trim()

    return (
        <div className="relative mt-2">
            {label && (
                <label className="block text-sm/6 font-medium text-gray-900 mb-1" htmlFor={id}>
                    {label}
                </label>
            )}
            <div className="relative">
                {asText ? (
                    <div className="flex items-center gap-2">
                        <span className={`block w-full rounded-md border-0 px-2 py-1 text-sm leading-6 ${
                            displayValue ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                            {displayValue || placeholder}
                        </span>
                        <button
                            type="button"
                            onClick={openModal}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                            aria-label="Adres bewerken"
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <>
                        <input
                            type="text"
                            id={id}
                            name={name}
                            placeholder={placeholder}
                            disabled={disabled}
                            required={required}
                            className={`block w-full rounded-md bg-white shadow-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 outline-none py-1.5 pr-10 pl-3 text-base placeholder:text-gray-400 sm:text-sm/6 ${className}`}
                            value={displayValue}
                            readOnly
                        />
                        <button
                            type="button"
                            onClick={openModal}
                            className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-gray-400 hover:text-primary focus:outline-hidden"
                            aria-label="Adres bewerken"
                        >
                            <PencilIcon className="size-5" aria-hidden="true"/>
                        </button>
                    </>
                )}
            </div>

            <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
                <DialogBackdrop className="fixed inset-0 bg-gray-500/50"/>
                <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                            <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                Adres
                            </DialogTitle>
                            <div className="mt-4">
                                <AddressFields value={draft} onChange={setDraft} idPrefix="address-dialog"/>
                            </div>
                            <div className="mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isCountryEmpty}
                                    className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50 sm:col-start-2"
                                >
                                    Opslaan
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                                >
                                    Annuleren
                                </button>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}
